import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { apiService, ApiError } from '../../services/api';
import { TradeLineInventoryItem } from '../../types/api';
import { DateRange } from 'react-day-picker';
import { 
  Loader2, 
  ExternalLink,
  Copy,
  Check,
  Search,
  Package,
  UserCheck,
  CheckCircle,
  DollarSign,
  ArrowLeftRight
} from 'lucide-react';
import { TradelineOrder, TradelineOrderStatus, TradelineOrderItem } from '../../types/tradeline-orders';
import { cn, formatCurrency, formatDateTime } from '../../lib/utils';
import { CreatedDateRangePicker } from './CreatedDateRangePicker';

// ── module-level helpers ────────────────────────────────────────────────────

const formatSSNDisplay = (ssn: string): string => {
  const d = ssn.replace(/\D/g, '');
  return d.length === 9 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : ssn;
};

const formatBirthDate = (birthDate: string): string => {
  try {
    const d = new Date(birthDate);
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getUTCFullYear()}`;
  } catch {
    return birthDate;
  }
};

const buildClipboardText = (order: TradelineOrder): string => {
  const u = order.user;
  if (!u) return '';
  const name = [u.firstName, u.middleName, u.lastName].filter(Boolean).join(' ');
  const address = [u.streetAddress, u.city, u.state, u.zip].filter(Boolean).join(', ');
  const lines: string[] = [];
  lines.push(`Full Name: ${name}`);
  lines.push(`Phone: ${u.phoneNumber}`);
  lines.push(`Email: ${u.email}`);
  if (u.birthDate) lines.push(`DOB: ${formatBirthDate(u.birthDate)}`);
  if (address) lines.push(`Address: ${address}`);
  if (u.ssn) lines.push(`SSN: ${formatSSNDisplay(u.ssn)}`);
  lines.push('');
  lines.push('Items:');
  (order.items || []).forEach((it) => {
    lines.push(`${it.name}${it.provider ? ` (${it.provider})` : ''} | $${it.price.toFixed(2)} x${it.quantity}`);
  });
  return lines.join('\n');
};

// ────────────────────────────────────────────────────────────────────────────

interface TradelineOrdersAdminProps {
  refreshTrigger?: number;
}

export function TradelineOrdersAdmin({ refreshTrigger = 0 }: TradelineOrdersAdminProps) {
  const [allOrders, setAllOrders] = useState<TradelineOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createdRange, setCreatedRange] = useState<DateRange | undefined>();
  const [page, setPage] = useState(1);
  const perPage = 25;

  // Order detail modal
  const [selectedOrder, setSelectedOrder] = useState<TradelineOrder | null>(null);
  const [selectedOrderUserName, setSelectedOrderUserName] = useState<string>('Open user profile');
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  
  // Action modals
  const [showLinesOrderedModal, setShowLinesOrderedModal] = useState(false);
  const [showLinesAddedModal, setShowLinesAddedModal] = useState(false);
  const [showLinesPostedModal, setShowLinesPostedModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  
  // Form states
  const [providerOrderId, setProviderOrderId] = useState('');
  const [bankAccountLast4, setBankAccountLast4] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [userFilter, setUserFilter] = useState('');
  const [debouncedUserFilter, setDebouncedUserFilter] = useState('');

  const [toastVisible, setToastVisible] = useState(false);
  const activeCopyOrderRef = useRef<string | null>(null);
  
  // Replace item modal
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceItemIndex, setReplaceItemIndex] = useState<number>(-1);
  const [replaceOriginalItem, setReplaceOriginalItem] = useState<TradelineOrderItem | null>(null);
  const [replaceReason, setReplaceReason] = useState('');
  const [replaceMode, setReplaceMode] = useState<'inventory' | 'custom'>('inventory');
  const [inventoryItems, setInventoryItems] = useState<TradeLineInventoryItem[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventorySearch, setInventorySearch] = useState('');
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<TradeLineInventoryItem | null>(null);
  const [replaceCustom, setReplaceCustom] = useState({
    tradeline_inventory_id: '',
    name: '',
    age_years: '',
    credit_limit: '',
    provider: '',
    quantity: '1',
  });
  const [replaceError, setReplaceError] = useState('');

  // Action loading states
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedUserFilter(userFilter);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [userFilter]);

  useEffect(() => {
    fetchOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, createdRange?.from?.getTime(), createdRange?.to?.getTime(), debouncedUserFilter]);

  const toUtcDayStartIso = (date: Date): string =>
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)).toISOString();

  const toUtcDayEndIso = (date: Date): string =>
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)).toISOString();

  useEffect(() => {
    if (refreshTrigger === 0) return;
    const refreshSilently = async () => {
      try {
        const params: any = {};
        if (statusFilter !== 'all') params.status = statusFilter;
        if (createdRange?.from) {
          params.start_date = toUtcDayStartIso(createdRange.from);
          params.end_date = toUtcDayEndIso(createdRange.to ?? createdRange.from);
        }
        if (debouncedUserFilter) params.search = debouncedUserFilter;
        const response = await apiService.getTradelineOrders(params);
        const responseData = response?.data;
        if (response?.success && Array.isArray(responseData)) {
          setAllOrders(responseData);
        } else if (response?.success && responseData && Array.isArray(responseData.orders)) {
          setAllOrders(responseData.orders);
        }
      } catch { /* keep stale data visible on error */ }
    };
    refreshSilently();
  }, [refreshTrigger]);

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const params: any = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (createdRange?.from) {
        params.start_date = toUtcDayStartIso(createdRange.from);
        params.end_date = toUtcDayEndIso(createdRange.to ?? createdRange.from);
      }
      if (debouncedUserFilter) params.search = debouncedUserFilter;

      const response = await apiService.getTradelineOrders(params);
      const responseData = response?.data;

      if (response?.success && Array.isArray(responseData)) {
        setAllOrders(responseData);
      } else if (response?.success && responseData && Array.isArray(responseData.orders)) {
        setAllOrders(responseData.orders);
      } else {
        setAllOrders([]);
      }
      setPage(1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load orders');
      setAllOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewOrder = async (orderId: string) => {
    try {
      const response = await apiService.getTradelineOrder(orderId);
      if (response.success) {
        const order = response.data as TradelineOrder;
        setSelectedOrder(order);
        setSelectedOrderUserName('Open user profile');
        setShowOrderDetail(true);

        try {
          const userResponse = await apiService.getAdminUser(order.user_id);
          const userData = userResponse?.data?.user || userResponse?.data || userResponse?.user || userResponse;
          const fullName = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim();
          setSelectedOrderUserName(fullName || userData?.email || 'Open user profile');
        } catch {
          // Keep fallback link label when user details are unavailable.
          setSelectedOrderUserName('Open user profile');
        }
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load order details');
    }
  };

  const getStatusBadge = (status: TradelineOrderStatus) => {
    const badges = {
      waiting_payment: 'text-yellow-800 border-yellow-200',
      payment_received: 'text-blue-800 border-blue-200',
      lines_ordered: 'text-purple-800 border-purple-200',
      lines_added: 'text-orange-800 border-orange-200',
      lines_posted: 'text-green-800 border-green-200',
      expired: 'text-gray-800 border-gray-200',
      cancelled: 'text-red-800 border-red-200',
      refunded: 'text-pink-800 border-pink-200',
    };
    
    const labels = {
      waiting_payment: 'Waiting Payment',
      payment_received: 'Payment Received',
      lines_ordered: 'Lines Ordered',
      lines_added: 'Lines Added',
      lines_posted: 'Lines Posted',
      expired: 'Expired',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const isAmexTradeline = (provider?: string, name?: string) => {
    const normalized = `${provider || ''} ${name || ''}`.toLowerCase();
    return normalized.includes('amex') || normalized.includes('american express');
  };

  const copyPaymentLink = async () => {
    if (selectedOrder?.payment_url) {
      try {
        await navigator.clipboard.writeText(selectedOrder.payment_url);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
    }
  };

  const doCopy = (order: TradelineOrder) => {
    const text = buildClipboardText(order);
    navigator.clipboard.writeText(text).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2500);
  };

  const handleCopyAndView = async (orderId: string) => {
    activeCopyOrderRef.current = orderId;
    try {
      const response = await apiService.getTradelineOrder(orderId);
      if (activeCopyOrderRef.current !== orderId) return;
      const order = response.data as TradelineOrder;
      setSelectedOrder(order);
      setSelectedOrderUserName(
        order.user ? `${order.user.firstName} ${order.user.lastName}`.trim() : 'Open user profile'
      );
      setShowOrderDetail(true);
      doCopy(order);
    } catch (err) {
      if (activeCopyOrderRef.current !== orderId) return;
      setError(err instanceof ApiError ? err.message : 'Failed to load order details');
    }
  };

  const handleMarkLinesOrdered = async () => {
    if (!selectedOrder) return;
    
    try {
      setIsProcessing(true);
      await apiService.markTradelineOrderLinesOrdered(selectedOrder._id, providerOrderId);
      setShowLinesOrderedModal(false);
      setProviderOrderId('');
      await handleViewOrder(selectedOrder._id);
      await fetchOrders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to mark lines as ordered');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarkLinesAdded = async () => {
    if (!selectedOrder) return;
    
    try {
      setIsProcessing(true);
      await apiService.markTradelineOrderLinesAdded(selectedOrder._id, bankAccountLast4);
      setShowLinesAddedModal(false);
      setBankAccountLast4('');
      await handleViewOrder(selectedOrder._id);
      await fetchOrders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to mark lines as added');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarkLinesPosted = async () => {
    if (!selectedOrder) return;
    
    try {
      setIsProcessing(true);
      await apiService.markTradelineOrderLinesPosted(selectedOrder._id);
      setShowLinesPostedModal(false);
      await handleViewOrder(selectedOrder._id);
      await fetchOrders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to mark lines as posted');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedOrder || !noteContent.trim()) return;
    
    try {
      setIsProcessing(true);
      await apiService.addTradelineOrderNote(selectedOrder._id, noteContent);
      setNoteContent('');
      await handleViewOrder(selectedOrder._id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to add note');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder || !cancelReason.trim()) return;
    
    try {
      setIsProcessing(true);
      await apiService.cancelTradelineOrder(selectedOrder._id, cancelReason);
      setShowCancelModal(false);
      setCancelReason('');
      setShowOrderDetail(false);
      await fetchOrders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to cancel order');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRefundOrder = async () => {
    if (!selectedOrder || !refundReason.trim()) return;
    
    try {
      setIsProcessing(true);
      await apiService.refundTradelineOrder(selectedOrder._id, refundReason);
      setShowRefundModal(false);
      setRefundReason('');
      setShowOrderDetail(false);
      await fetchOrders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to refund order');
    } finally {
      setIsProcessing(false);
    }
  };

  const fetchInventory = useCallback(async () => {
    if (inventoryItems.length > 0) return;
    try {
      setInventoryLoading(true);
      const response = await apiService.getTradeLineInventory();
      if (response.success) {
        setInventoryItems(response.inventory);
      }
    } catch {
      // inventory fetch is best-effort; admin can use custom mode
    } finally {
      setInventoryLoading(false);
    }
  }, [inventoryItems.length]);

  const openReplaceModal = (index: number, item: TradelineOrderItem) => {
    setReplaceItemIndex(index);
    setReplaceOriginalItem(item);
    setReplaceReason('');
    setReplaceMode('inventory');
    setSelectedInventoryItem(null);
    setInventorySearch('');
    setReplaceCustom({
      tradeline_inventory_id: '',
      name: '',
      age_years: '',
      credit_limit: '',
      provider: '',
      quantity: '1',
    });
    setReplaceError('');
    setShowReplaceModal(true);
    fetchInventory();
  };

  const handleReplaceItem = async () => {
    if (!selectedOrder || !replaceOriginalItem) return;

    let newItem: {
      tradeline_inventory_id: string;
      name: string;
      age_years: number;
      credit_limit: number;
      price: number;
      provider: string;
      quantity: number;
    };

    if (replaceMode === 'inventory') {
      if (!selectedInventoryItem) {
        setReplaceError('Select a tradeline from inventory.');
        return;
      }
      const now = new Date();
      const openDate = new Date(selectedInventoryItem.open_date);
      const ageYears = Math.max(0, (now.getTime() - openDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));

      newItem = {
        tradeline_inventory_id: selectedInventoryItem.id,
        name: selectedInventoryItem.card_name,
        age_years: Math.round(ageYears * 100) / 100,
        credit_limit: selectedInventoryItem.credit_limit,
        price: replaceOriginalItem.price,
        provider: selectedInventoryItem.provider,
        quantity: replaceOriginalItem.quantity,
      };
    } else {
      if (!replaceCustom.name.trim() || !replaceCustom.tradeline_inventory_id.trim()) {
        setReplaceError('Name and Inventory ID are required for custom entries.');
        return;
      }
      newItem = {
        tradeline_inventory_id: replaceCustom.tradeline_inventory_id.trim(),
        name: replaceCustom.name.trim(),
        age_years: parseFloat(replaceCustom.age_years) || 0,
        credit_limit: parseFloat(replaceCustom.credit_limit) || 0,
        price: replaceOriginalItem.price,
        provider: replaceCustom.provider.trim(),
        quantity: parseInt(replaceCustom.quantity, 10) || 1,
      };
    }

    if (!replaceReason.trim()) {
      setReplaceError('Reason is required.');
      return;
    }

    try {
      setIsProcessing(true);
      setReplaceError('');
      await apiService.replaceTradelineOrderItem(
        selectedOrder._id,
        replaceItemIndex,
        newItem,
        replaceReason.trim()
      );
      setShowReplaceModal(false);
      await handleViewOrder(selectedOrder._id);
      await fetchOrders();
    } catch (err) {
      setReplaceError(err instanceof ApiError ? err.message : 'Failed to replace item');
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredInventory = inventoryItems.filter((inv) => {
    if (!inventorySearch) return true;
    const q = inventorySearch.toLowerCase();
    return (
      inv.card_name.toLowerCase().includes(q) ||
      inv.provider.toLowerCase().includes(q) ||
      inv.id.toLowerCase().includes(q)
    );
  });

  const canEditItems = selectedOrder && ['payment_received', 'lines_ordered'].includes(selectedOrder.status);

  const getExpirationDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 3);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getLifecycleProgressIndex = (status: TradelineOrderStatus): number => {
    switch (status) {
      case 'waiting_payment':
        return 0;
      case 'payment_received':
        return 1;
      case 'lines_ordered':
        return 2;
      case 'lines_added':
        return 3;
      case 'lines_posted':
      case 'expired':
      case 'cancelled':
      case 'refunded':
        return 4;
      default:
        return 0;
    }
  };

  const formatNoteTimestamp = (note: any): string => {
    const createdAt = note?.created_at || note?.createdAt;
    if (!createdAt) return '-';
    try {
      return formatDateTime(createdAt);
    } catch {
      return '-';
    }
  };

  const total = allOrders.length;
  const totalPages = Math.ceil(total / perPage);
  const orders = allOrders.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="w-full -mt-5 space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold text-foreground">Tradeline Orders</h1>
        <div className="flex gap-2 flex-wrap sm:flex-nowrap sm:ml-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              placeholder="Filter by user email..."
              className="pl-9 h-10 min-w-[200px]"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="min-w-[130px] w-auto h-10 rounded-lg">
              <span className="text-sm whitespace-nowrap">
                <span className="text-gray-500">Statuses: </span>
                <span className="font-medium">
                  {statusFilter === 'all'
                    ? 'All'
                    : statusFilter
                        .split('_')
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')}
                </span>
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="waiting_payment">Waiting Payment</SelectItem>
              <SelectItem value="payment_received">Payment Received</SelectItem>
              <SelectItem value="lines_ordered">Lines Ordered</SelectItem>
              <SelectItem value="lines_added">Lines Added</SelectItem>
              <SelectItem value="lines_posted">Lines Posted</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>

          <CreatedDateRangePicker
            value={createdRange}
            showLabel={false}
            className="min-w-[160px]"
            onChange={(range) => {
              setCreatedRange(range);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {/* Orders Table */}
      {isLoading && orders.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-gray-200">
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Tradelines</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">User</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Total</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Items</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Status</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Created</TableHead>
              </TableRow>
            </TableHeader>
          </Table>
          <div className="text-center py-16">
            <div className="flex items-center justify-center mx-auto mb-4 bg-gray-100" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Yet</h3>
            <p className="text-sm text-gray-500">
              No tradeline orders found for the selected filters.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="border border-border rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-gray-200">
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Tradelines</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Total</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Items</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Status</TableHead>
                  <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow
                    key={order._id}
                    className="h-14 hover:bg-gray-50 border-b border-gray-200 cursor-pointer"
                    onClick={() => handleViewOrder(order._id)}
                  >
                    <TableCell className="py-2 px-4 text-sm">
                      {order.items && order.items.length > 0 ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-900 truncate max-w-[240px]">{order.items[0].name}{order.items[0].provider ? ` (${order.items[0].provider})` : ''}</span>
                          {order.items.length > 1 && (
                            <span className="inline-flex items-center shrink-0 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              +{order.items.length - 1} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2 px-4">
                      <div className="flex items-center gap-1 max-w-[200px]">
                        <div className="flex flex-col min-w-0">
                          {order.user && (
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {order.user.firstName} {order.user.lastName}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 truncate">
                            {order.user?.email || order.user_email}
                          </span>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleCopyAndView(order._id); }}
                          className="flex-shrink-0 p-0.5 text-gray-400 hover:text-gray-700 rounded transition-colors"
                          title="Copy order info"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="py-2 px-4 text-sm font-medium">
                      ${order.total_amount.toFixed(2)}
                    </TableCell>
                    <TableCell className="py-2 px-4 text-sm">
                      {order.items ? order.items.reduce((sum, item) => sum + item.quantity, 0) : 0}
                    </TableCell>
                    <TableCell className="py-2 px-4">{getStatusBadge(order.status)}</TableCell>
                    <TableCell className="py-2 px-4 text-sm text-gray-600">
                      {formatDateTime(order.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {(page - 1) * perPage + 1}-{Math.min(page * perPage, total)} of {total} orders
              </div>

              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || isLoading}
                  className="h-9 text-sm"
                >
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant="ghost"
                        onClick={() => setPage(pageNum)}
                        disabled={isLoading}
                        className={cn(
                          "h-9 w-9 text-sm rounded-lg",
                          page === pageNum
                            ? "border border-gray-300 bg-white text-gray-900 font-medium hover:bg-gray-50"
                            : "text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}

                  {totalPages > 5 && page < totalPages - 2 && (
                    <>
                      <span className="px-2 text-muted-foreground">...</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPage(totalPages)}
                        disabled={isLoading}
                        className="h-9 w-9 text-sm"
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}
                </div>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || isLoading}
                  className="h-9 text-sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Order Detail Modal - Will be implemented in next part */}
      {/* Action Modals - Will be implemented in next part */}
      
      {/* Order Detail Modal */}
      <Dialog open={showOrderDetail} onOpenChange={setShowOrderDetail}>
        <DialogContent
          className="!h-screen !max-w-none !translate-x-0 !translate-y-0 !top-0 !left-auto !right-0 !rounded-none !data-[state=closed]:slide-out-to-right !data-[state=open]:slide-in-from-right !flex !flex-col !justify-start !items-start !p-0 !gap-0 [&>button]:!right-6 [&>button]:!top-6"
          style={{ width: '900px' }}
        >
          {selectedOrder && (
            <div className="space-y-6 w-full h-full overflow-y-auto" style={{ padding: '24px' }}>
              {/* Header */}
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">Order #{selectedOrder._id.substring(0, 8)}</h2>
                <div className="mt-2 flex items-center gap-4">
                  {getStatusBadge(selectedOrder.status)}
                  <span className="text-sm text-gray-600">
                    Created: {formatDateTime(selectedOrder.created_at)}
                  </span>
                </div>
              </div>

              {/* Order Items */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Order Items</h3>
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  {selectedOrder.items && selectedOrder.items.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent border-b border-gray-200">
                          <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Tradeline</TableHead>
                          <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Age</TableHead>
                          <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Credit Limit</TableHead>
                          <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Price</TableHead>
                          <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Qty</TableHead>
                          <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500 text-right">Total</TableHead>
                          {canEditItems && (
                            <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500 text-right w-[1%]"></TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedOrder.items.map((item, idx) => (
                          <TableRow key={`${selectedOrder._id}-${item.tradeline_inventory_id}-${idx}`} className="border-b border-gray-200">
                            <TableCell className="py-2 px-4">
                              <div className="text-sm text-gray-900">{item.name}{item.provider ? <span className="text-gray-500"> ({item.provider})</span> : ''}</div>
                              <div className="text-[11px] text-gray-400 font-mono mt-0.5">{item.tradeline_inventory_id}</div>
                            </TableCell>
                            <TableCell className="py-2 px-4 text-sm text-gray-700">
                              {isAmexTradeline(item.provider, item.name) ? '-' : `${item.age_years.toFixed(2)} years`}
                            </TableCell>
                            <TableCell className="py-2 px-4 text-sm text-gray-700">{formatCurrency(item.credit_limit)}</TableCell>
                            <TableCell className="py-2 px-4 text-sm text-gray-700">{formatCurrency(item.price)}</TableCell>
                            <TableCell className="py-2 px-4 text-sm text-gray-700">{item.quantity}</TableCell>
                            <TableCell className="py-2 px-4 text-sm text-gray-900 font-medium text-right">
                              {formatCurrency(item.price * item.quantity)}
                            </TableCell>
                            {canEditItems && (
                              <TableCell className="py-2 px-4 text-right whitespace-nowrap">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs text-gray-500 hover:text-gray-900 gap-1"
                                  onClick={() => openReplaceModal(idx, item)}
                                >
                                  <ArrowLeftRight className="h-3.5 w-3.5" />
                                  Replace
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="px-4 py-4 text-sm text-gray-600">No order items found.</div>
                  )}
                </div>
              </div>

              {/* Payment Information */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Payment Information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Total:</span>
                    <span className="text-sm font-semibold text-gray-900">{formatCurrency(selectedOrder.total_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Stripe Invoice:</span>
                    <span className="text-sm font-mono text-gray-900">{selectedOrder.stripe_invoice_id || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">User:</span>
                    <a
                      href={`/admin?userId=${selectedOrder.user_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 underline"
                    >
                      {selectedOrderUserName}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                  {selectedOrder.payment_url && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => window.open(selectedOrder.payment_url, '_blank')}
                      >
                        View in Stripe <ExternalLink className="h-4 w-4 ml-2" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={copyPaymentLink}
                      >
                        {copiedLink ? (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Link Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Payment Link
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Lifecycle Timeline */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Lifecycle Timeline</h3>
                <div className="relative overflow-x-auto">
                  <div className="relative min-w-[720px]">
                    <div
                      className="absolute top-[6px] left-[6px] h-0.5 bg-gray-300"
                      style={{ width: 'calc(100% - 120px)' }}
                    ></div>
                    <div className="flex items-start justify-between gap-8">
                      {[
                        { label: 'Created', detail: formatDateTime(selectedOrder.created_at), index: 0 },
                        {
                          label: 'Payment Received',
                          detail: getLifecycleProgressIndex(selectedOrder.status) >= 1 ? formatDateTime(selectedOrder.updated_at) : '-',
                          index: 1
                        },
                        {
                          label: 'Lines Ordered',
                          detail: getLifecycleProgressIndex(selectedOrder.status) >= 2
                            ? (selectedOrder.provider_order_id ? `Order ID: ${selectedOrder.provider_order_id}` : 'Ordered')
                            : '-',
                          index: 2
                        },
                        {
                          label: 'Lines Added',
                          detail: getLifecycleProgressIndex(selectedOrder.status) >= 3
                            ? (selectedOrder.bank_account_last4 ? `Account: ****${selectedOrder.bank_account_last4}` : 'Added')
                            : '-',
                          index: 3
                        },
                        {
                          label: 'Lines Posted',
                          detail: getLifecycleProgressIndex(selectedOrder.status) >= 4
                            ? (selectedOrder.posted_date ? formatDateTime(selectedOrder.posted_date) : 'Posted')
                            : '-',
                          index: 4
                        },
                      ].map((step) => {
                        const isCompleted = step.index <= getLifecycleProgressIndex(selectedOrder.status);
                        return (
                          <div key={step.label} className="flex flex-col relative z-10 w-[120px]">
                            <div
                              className={`w-[12px] h-[12px] rounded-full border-[3px] bg-white ${
                                isCompleted ? 'border-black' : 'border-gray-300'
                              }`}
                            />
                            <span className={`text-xs mt-2 whitespace-nowrap ${isCompleted ? 'font-medium text-gray-900' : 'text-gray-400'}`}>
                              {step.label}
                            </span>
                            <span className={`text-xs whitespace-nowrap ${isCompleted ? 'text-gray-500' : 'text-gray-400'}`}>
                              {step.detail}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Admin Actions */}
              {['payment_received', 'lines_ordered', 'lines_added'].includes(selectedOrder.status) && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Admin Actions</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedOrder.status === 'payment_received' && (
                      <Button
                        onClick={() => setShowLinesOrderedModal(true)}
                        variant="outline"
                        className="gap-2"
                      >
                        <Package className="h-4 w-4" />
                        Mark Lines Ordered
                      </Button>
                    )}
                    
                    {selectedOrder.status === 'lines_ordered' && (
                      <Button
                        onClick={() => setShowLinesAddedModal(true)}
                        variant="outline"
                        className="gap-2"
                      >
                        <UserCheck className="h-4 w-4" />
                        Mark Lines Added
                      </Button>
                    )}
                    
                    {selectedOrder.status === 'lines_added' && (
                      <Button
                        onClick={() => setShowLinesPostedModal(true)}
                        variant="outline"
                        className="gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Mark Lines Posted
                      </Button>
                    )}
                    
                    {['payment_received', 'lines_ordered', 'lines_added'].includes(selectedOrder.status) && (
                      <Button
                        onClick={() => setShowRefundModal(true)}
                        variant="outline"
                        className="gap-2 text-red-600 hover:text-red-700"
                      >
                        <DollarSign className="h-4 w-4" />
                        Refund Order
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">
                  Admin Notes ({selectedOrder.notes?.length || 0})
                </h3>
                
                {selectedOrder.notes && selectedOrder.notes.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {selectedOrder.notes.map((note) => (
                      <div key={note._id} className="bg-gray-50 rounded-md p-3">
                        <p className="text-sm text-gray-900">{note.content}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {note.admin_name || 'Admin'} • {formatNoteTimestamp(note)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Add Note:</label>
                  <textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Type your note here..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                    rows={3}
                  />
                  <Button
                    onClick={handleAddNote}
                    disabled={!noteContent.trim() || isProcessing}
                    className="bg-black hover:bg-black/90 text-white w-full"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      'Add Note'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Lines Ordered Modal */}
      <Dialog open={showLinesOrderedModal} onOpenChange={setShowLinesOrderedModal}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Mark Lines as Ordered</h2>
              <p className="text-sm text-gray-600 mt-2">
                You've placed the order with the tradeline provider. Enter the provider's order ID.
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Provider Order ID (optional):
              </label>
              <Input
                value={providerOrderId}
                onChange={(e) => setProviderOrderId(e.target.value)}
                placeholder="ORD-12345"
                className="w-full"
              />
            </div>
            
            <p className="text-sm text-gray-600">
              This will mark the order as "Lines Ordered".
            </p>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowLinesOrderedModal(false);
                  setProviderOrderId('');
                }}
                disabled={isProcessing}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkLinesOrdered}
                disabled={isProcessing}
                className="bg-black hover:bg-black/90 text-white px-4"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark Lines Added Modal */}
      <Dialog open={showLinesAddedModal} onOpenChange={setShowLinesAddedModal}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Mark Lines as Added</h2>
              <p className="text-sm text-gray-600 mt-2">
                Customer has been added as authorized user on the tradeline accounts.
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Bank Account Last 4 Digits (optional):
              </label>
              <Input
                value={bankAccountLast4}
                onChange={(e) => setBankAccountLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="1234"
                maxLength={4}
                className="w-full"
              />
            </div>
            
            <p className="text-sm text-gray-600">
              This will mark the order as "Lines Added".
            </p>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowLinesAddedModal(false);
                  setBankAccountLast4('');
                }}
                disabled={isProcessing}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkLinesAdded}
                disabled={isProcessing}
                className="bg-black hover:bg-black/90 text-white px-4"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark Lines Posted Modal */}
      <Dialog open={showLinesPostedModal} onOpenChange={setShowLinesPostedModal}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Mark Lines as Posted</h2>
              <p className="text-sm text-gray-600 mt-2">
                Tradelines are now visible on customer's credit report.
              </p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm text-amber-800">
                ⚠️ This will set the expiration date to 3 months from now.
              </p>
            </div>
            
            <div>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Expiration Date:</span> {getExpirationDate()}
              </p>
            </div>
            
            <p className="text-sm text-gray-600">
              This will mark the order as "Lines Posted".
            </p>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowLinesPostedModal(false)}
                disabled={isProcessing}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkLinesPosted}
                disabled={isProcessing}
                className="bg-black hover:bg-black/90 text-white px-4"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Order Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Cancel Order</h2>
              <p className="text-sm text-gray-600 mt-2">
                ⚠️ This will cancel the unpaid invoice and restore inventory.
              </p>
            </div>
            
            {selectedOrder && (
              <div className="bg-gray-50 rounded-md p-3 space-y-1">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Current Status:</span> {selectedOrder.status.replace('_', ' ')}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Total Amount:</span> ${selectedOrder.total_amount.toFixed(2)}
                </p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Reason for cancellation:
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Customer requested cancellation"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                disabled={isProcessing}
                className="px-4"
              >
                Go Back
              </Button>
              <Button
                onClick={handleCancelOrder}
                disabled={!cancelReason.trim() || isProcessing}
                className="bg-red-600 hover:bg-red-700 text-white px-4"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Cancel Order'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund Order Modal */}
      <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Refund Order</h2>
              <p className="text-sm text-gray-600 mt-2">
                ⚠️ This will process a full refund via Stripe and restore inventory.
              </p>
            </div>
            
            {selectedOrder && (
              <div className="bg-gray-50 rounded-md p-3 space-y-1">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Current Status:</span> {selectedOrder.status.replace('_', ' ')}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Total Amount:</span> ${selectedOrder.total_amount.toFixed(2)}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Refund Amount:</span> ${selectedOrder.total_amount.toFixed(2)} (full refund)
                </p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Reason for refund:
              </label>
              <textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Provider unable to fulfill order"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRefundModal(false);
                  setRefundReason('');
                }}
                disabled={isProcessing}
                className="px-4"
              >
                Go Back
              </Button>
              <Button
                onClick={handleRefundOrder}
                disabled={!refundReason.trim() || isProcessing}
                className="bg-red-600 hover:bg-red-700 text-white px-4"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Process Refund'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Replace Item Modal */}
      <Dialog open={showReplaceModal} onOpenChange={setShowReplaceModal}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Replace Tradeline Item</h2>
              {replaceOriginalItem && (
                <div className="mt-2 bg-gray-50 rounded-md p-3 space-y-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Replacing</p>
                  <p className="text-sm font-semibold text-gray-900">{replaceOriginalItem.name}</p>
                  <p className="text-xs text-gray-500">
                    {replaceOriginalItem.provider}
                    {isAmexTradeline(replaceOriginalItem.provider, replaceOriginalItem.name)
                      ? ''
                      : ` • ${replaceOriginalItem.age_years.toFixed(2)} yrs`}
                    {' • '}
                    {formatCurrency(replaceOriginalItem.credit_limit)} limit • Price: {formatCurrency(replaceOriginalItem.price)}
                  </p>
                </div>
              )}
            </div>

            {/* Mode toggle */}
            <div className="flex gap-2">
              <Button
                variant={replaceMode === 'inventory' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setReplaceMode('inventory')}
                className={replaceMode === 'inventory' ? 'bg-black hover:bg-black/90 text-white' : ''}
              >
                From Inventory
              </Button>
              <Button
                variant={replaceMode === 'custom' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setReplaceMode('custom')}
                className={replaceMode === 'custom' ? 'bg-black hover:bg-black/90 text-white' : ''}
              >
                Custom Entry
              </Button>
            </div>

            {replaceMode === 'inventory' ? (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={inventorySearch}
                    onChange={(e) => setInventorySearch(e.target.value)}
                    placeholder="Search by name, provider, or ID..."
                    className="pl-9"
                  />
                </div>

                {inventoryLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg max-h-[240px] overflow-y-auto">
                    {filteredInventory.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-gray-500">
                        No matching tradelines found.
                      </div>
                    ) : (
                      filteredInventory.map((inv) => {
                        const isSelected = selectedInventoryItem?.id === inv.id;
                        return (
                          <div
                            key={inv.id}
                            className={`px-4 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors ${
                              isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => setSelectedInventoryItem(inv)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {isSelected && <Check className="h-4 w-4 text-gray-900 flex-shrink-0" />}
                                  <span className="text-sm font-medium text-gray-900 truncate">{inv.card_name}</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {inv.provider} • {formatCurrency(inv.credit_limit)} limit • {inv.available_slots} slots
                                </div>
                              </div>
                              <div className="text-xs text-gray-400 font-mono flex-shrink-0 ml-3">
                                {formatCurrency(inv.cost_per_tradeline)}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">Tradeline Name *</label>
                    <Input
                      value={replaceCustom.name}
                      onChange={(e) => setReplaceCustom(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Chase Freedom Unlimited"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">Inventory ID *</label>
                    <Input
                      value={replaceCustom.tradeline_inventory_id}
                      onChange={(e) => setReplaceCustom(prev => ({ ...prev, tradeline_inventory_id: e.target.value }))}
                      placeholder="custom-uuid"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">Provider</label>
                    <Input
                      value={replaceCustom.provider}
                      onChange={(e) => setReplaceCustom(prev => ({ ...prev, provider: e.target.value }))}
                      placeholder="BoostCredit"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">Age (years)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={replaceCustom.age_years}
                      onChange={(e) => setReplaceCustom(prev => ({ ...prev, age_years: e.target.value }))}
                      placeholder="5.0"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">Credit Limit ($)</label>
                    <Input
                      type="number"
                      value={replaceCustom.credit_limit}
                      onChange={(e) => setReplaceCustom(prev => ({ ...prev, credit_limit: e.target.value }))}
                      placeholder="15000"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 block mb-1">Quantity</label>
                    <Input
                      type="number"
                      min="1"
                      value={replaceCustom.quantity}
                      onChange={(e) => setReplaceCustom(prev => ({ ...prev, quantity: e.target.value }))}
                      placeholder="1"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Price note */}
            {replaceOriginalItem && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-sm text-amber-800">
                  Price is locked to <span className="font-semibold">{formatCurrency(replaceOriginalItem.price)}</span> (must match the original item).
                </p>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Reason for replacement *
              </label>
              <textarea
                value={replaceReason}
                onChange={(e) => setReplaceReason(e.target.value)}
                placeholder="Original tradeline out of stock, replaced with equivalent limit/age"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                rows={2}
              />
            </div>

            {replaceError && (
              <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                {replaceError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowReplaceModal(false)}
                disabled={isProcessing}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReplaceItem}
                disabled={isProcessing || !replaceReason.trim() || (replaceMode === 'inventory' && !selectedInventoryItem) || (replaceMode === 'custom' && (!replaceCustom.name.trim() || !replaceCustom.tradeline_inventory_id.trim()))}
                className="bg-black hover:bg-black/90 text-white px-4"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Replacing...
                  </>
                ) : (
                  <>
                    <ArrowLeftRight className="h-4 w-4 mr-2" />
                    Replace Item
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Toast */}
      {toastVisible && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] bg-gray-900 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 pointer-events-none">
          <Check className="h-4 w-4 text-green-400" />
          Copied to clipboard
        </div>
      )}
    </div>
  );
}
